import * as H from 'history';
import * as queryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { DeprecatedEventWrapper } from '../../../common/analyticsEvents/DeprecatedEventWrapper';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { DevicePreviewShowPreviewEvent, TelemetryEvents } from '../../../common/telemetryEvents/TelemetryEvents';
import { IVizViewerScope } from '../../../ts/controllers/VizViewerCtrl';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { CommonErrorCode } from '../../../ts/helpers/ErrorCodes';
import { isCurrentUrlViz } from '../../../ts/helpers/UrlHelpers';
import { ViewDefinition } from '../../../ts/models/ContentDefinitions';
import { ViewContentType } from '../../../ts/models/ContentTypes';
import { IDataAlertInfo } from '../../../ts/models/IDataAlertInfo';
import { IViewDetailInfo } from '../../../ts/models/IViewInfo';
import { IExplorableItemTypeInfo, INamedItemInfo } from '../../../ts/models/resourceInfoParts';
import { IViz, VizType } from '../../../ts/models/Viz';
import { DataAlerts } from '../../../ts/resources/DataAlerts';
import { DataSources } from '../../../ts/resources/Datasources';
import { Projects } from '../../../ts/resources/Projects';
import { Views } from '../../../ts/resources/Views';
import { IViewActions } from '../../../ts/ServerApi/types/IViewActions';
import { BrowserTitleService } from '../../../ts/services/BrowserTitleService';
import { DeprecatedAnalyticsService } from '../../../ts/services/DeprecatedAnalyticsService';
import { ServerService } from '../../../ts/services/ServerService';
import { TelemetryService } from '../../../ts/services/TelemetryService';
import { VizService } from '../../../ts/services/VizService';
import { enterSpace, setSpaceInfoItem } from '../../actions/spaces/EnterSpaceAction';
import { setVizPath } from '../../actions/spaces/SetVizPathAction';
import { appStore } from '../../AppStore';
import { addContentTypeToProjectHierarchy, populateContentBreadcrumbs } from '../../content/breadcrumbs/BreadcrumbHelpers';
import { DevicePreviewModalBuilder } from '../../content/devicePreview/DevicePreviewModalBuilder';
import { VizPathInitialState } from '../../reducers/VizPathReducer';
import { selectLayoutType } from '../../selectors/LayoutTypeSelector';
import { SessionSiteSelectors } from '../../selectors/SessionInfoSelectors';
import { selectVizPath } from '../../selectors/VizPathSelector';
import { ServiceLocator } from '../../ServiceLocator';
import { LayoutType } from '../../state/LayoutType';
import { SpaceInfo } from '../../state/SpaceInfo';
import { UrlParamKeys } from '../../UrlParamKeys';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { RequestAccessError } from '../RouterHelpers/RequestAccessError';
import { RouterError, RouterErrorOwnProps } from '../RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '../SpaceTelemetry';
import { VizUrl } from './VizUrlHelpers';

export interface VizSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly vizPathFromUrl: string;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
  readonly renderSpace: (angularContext: AngularContext, setupScope: (scope: ng.IScope) => ng.IScope) => JSX.Element;
}

export interface VizSpaceProviderStateProps {
  readonly vizPath: string;
  readonly sessionSiteUrlName: string;
  readonly layoutType: LayoutType;
  readonly isRequestAccessEnabled: boolean;
}

export interface VizSpaceProviderDispatchProps {
  readonly setVizPath: (vizPath: string) => void;
  readonly setSpaceInfoItem: (spaceInfo: SpaceInfo) => void;
}

export interface VizSpaceProviderAngularProps {
  readonly browserTitleService: BrowserTitleService;
  readonly server: ServerService;
  readonly views: Views;
  readonly dataAlerts: DataAlerts;
  readonly deprecatedAnalytics: DeprecatedAnalyticsService;
  readonly telemetry: TelemetryService;
  readonly dataSources: DataSources;
  readonly vizService: VizService;
  readonly projects: Projects;
}

export interface VizSpaceProviderState {
  readonly viewInfo: IViewDetailInfo;
  readonly errorCode: Number;
  readonly hasError: boolean;
  readonly errorVizPath: string;
  readonly hasDataAlertError: boolean;
  readonly dataAlertRouterErrorProps: RouterErrorOwnProps;
}

const initialNoErrorState: VizSpaceProviderState = {
  viewInfo: undefined,
  errorCode: undefined,
  hasError: false,
  hasDataAlertError: false,
  dataAlertRouterErrorProps: undefined,
  errorVizPath: undefined
};

const template = `<div ng-include="'vizViewer.html'"></div>`;

export const renderVizSpace = (angularContext: AngularContext, setupScope: (scope: ng.IScope) => ng.IScope): JSX.Element => {
  // the parent div here is important to ensure AngularDirective plays nice when unmounting
  return (
    <div>
      <AngularDirective
        template={template}
        angularContext={angularContext}
        setupScope={setupScope}
      />
    </div>
  );
};

export type VizSpaceProviderPropsWithoutAngular = VizSpaceProviderOwnProps & VizSpaceProviderStateProps & VizSpaceProviderDispatchProps;
export type VizSpaceProviderProps = VizSpaceProviderPropsWithoutAngular & VizSpaceProviderAngularProps;

export class VizSpaceProvider extends React.Component<VizSpaceProviderProps, VizSpaceProviderState> {

  private spaceScope: IVizViewerScope = null;
  private unmounted: boolean = false;

  public constructor(props: VizSpaceProviderProps) {
    super(props);
    this.state = initialNoErrorState;
  }

  public render() {
    if (this.state.hasError) {
      if (this.state.hasDataAlertError) {
        return <RouterError {...this.state.dataAlertRouterErrorProps} />;
      }
      if (this.props.isRequestAccessEnabled) {
        return <RequestAccessError
          url={this.props.urls.exploreUrl()}
          contentIdentifier={this.state.errorVizPath}
          contentType={'view'}
          errorCode={this.state.errorCode}/>;
      }

      // old error page
      return <RouterError urls={this.props.urls} errorType="view"/>;
    } else if (!this.state.viewInfo) {
      return null;
    }

    // have view info and no errors, render the viz
    return this.props.renderSpace(this.props.angularContext, this.setupScope);
  }

  public componentDidMount() {
    // set initial viz path
    this.props.setVizPath(this.props.vizPathFromUrl);

    this.getViewInfoAndUpdateBrowserTitle();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public getViewInfoAndUpdateBrowserTitle = async () => {
    try {
      const viewInfo: IViewDetailInfo = await this.props.views.getByPath(this.props.vizPathFromUrl);
      this.updateState({...initialNoErrorState, viewInfo: viewInfo});
      this.props.browserTitleService.setVizTitle(viewInfo.name, viewInfo.workbook.name);
      this.props.setSpaceInfoItem({item: viewInfo, spaceType: 'view', subspaceName: undefined});

    } catch (errorCode) {
      this.updateState({
        viewInfo: undefined,
        errorCode: errorCode,
        errorVizPath: this.props.vizPathFromUrl,
        hasError: true,
        hasDataAlertError: false,
        dataAlertRouterErrorProps: undefined
      });
    }
  }

  public componentDidUpdate(prevProps: VizSpaceProviderProps) {
    const prevVizPathFromUrl = prevProps.vizPathFromUrl;
    const currVizPathFromUrl = this.props.vizPathFromUrl;
    const vizPathFromAppState = this.props.vizPath;

    if (prevVizPathFromUrl !== currVizPathFromUrl && currVizPathFromUrl !== vizPathFromAppState) {
      // the url changed (because the app state vizPath is different from the new route props)
      // but the vizPath is not updated yet. So update it and reload the viz.
      this.props.setVizPath(currVizPathFromUrl);

      if (this.spaceScope) {
        const previousViz: IViz = this.spaceScope.viz;

        this.spaceScope.viz = {
          path: currVizPathFromUrl,
          vizType: VizType.View,
          params: VizUrl.getVizParams(this.props.routeLocationSearch || ''),
          allowReloadViz: true,  // this needs to be true so we can reload the viz when the url changes

          reloadViz: previousViz.reloadViz,

          instanceId: VizUrl.getInstanceId(this.props.routeLocationSearch),
          isExplicitOriginalViz: VizUrl.getIsExplicitOriginalVizFromUrl(window.location.href),
        };
        this.spaceScope.$apply();
        this.spaceScope.viz.reloadViz && this.spaceScope.viz.reloadViz();

      } else {
        // since there is no spaceScope, that probably means we were showing the error page before.
        // fetch the view here and then keep going
        this.getViewInfoAndUpdateBrowserTitle();
      }
    }
  }

  public shouldComponentUpdate(nextProps: VizSpaceProviderProps) {
    const nextVizPathFromUrl = nextProps.vizPathFromUrl;
    if (nextProps.vizPath !== VizPathInitialState &&
      nextProps.vizPath !== this.props.vizPath &&
      nextProps.vizPath !== nextVizPathFromUrl) {
      // vizPath changed via user clicking on viz tab (that's why the url viz path does not match the new app state vizPath-- see tbViz),
      // so don't rerender
      return false;
    }

    return true;
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as IVizViewerScope;
    childScope.updateVizProviderState = this.updateState;

    childScope.viz = {
      path: this.props.vizPathFromUrl,
      vizType: VizType.View,
      params: VizUrl.getVizParams(this.props.routeLocationSearch),
      siteUrlName: this.props.server.sessionSiteUrlName(),
      instanceId: VizUrl.getInstanceId(this.props.routeLocationSearch),
      isExplicitOriginalViz: VizUrl.getIsExplicitOriginalVizFromUrl(window.location.href),
      allowReloadViz: true
    };


    childScope.displayRoutingError = false;
    childScope.displayDataAlertRoutingError = false;
    childScope.requestAccessEnabled = this.props.server.isRequestAccessEnabled();

    childScope.deprecatedEventFactory = this.props.deprecatedAnalytics.EventFactory(DeprecatedEventWrapper.eventTypes.VisitAlertFromEmail);

    childScope.onVizSized = () => childScope.vizInitialized = true;

    childScope.vizProps = {};

    const siteUrls = new SpacesSiteUrls({siteUrlName: this.props.server.sessionSiteUrlName(), queryString: window.location.search});
    const handleDevicePreviewClick = () => {
      this.props.telemetry.sendEvent<DevicePreviewShowPreviewEvent>({
        type: TelemetryEvents.DevicePreviewShowPreview
      });

      DevicePreviewModalBuilder.openPreview(this.props.angularContext, childScope.viz.path);
    };

    childScope.vizViewerWidgetsBarProps = {
      view: null,
      siteUrls: siteUrls,
      dataSources: this.props.dataSources,
      showDevicePreview: false,
      handleDevicePreviewClick: handleDevicePreviewClick,
      layoutType: selectLayoutType(appStore.getState()),
      telemetryService: this.props.telemetry
    };

    function displayInvalidVizError(errorCode: Number, vizPath: string) {
      childScope.displayRoutingError = true;
      childScope.displayDataAlertRoutingError = false;

      childScope.updateVizProviderState({
        hasError: true,
        errorCode: errorCode,
        errorVizPath: vizPath,
        viewInfo: undefined,
        hasDataAlertError: false,
        dataAlertRouterErrorProps: undefined,
      });
    }

    function displayDataAlertError(dataAlertRouterErrorProps: RouterErrorOwnProps) {
      childScope.displayRoutingError = true;
      childScope.displayDataAlertRoutingError = true;

      childScope.updateVizProviderState({
        hasError: true,
        errorCode: undefined,
        errorVizPath: undefined,
        viewInfo: undefined,
        hasDataAlertError: true,
        dataAlertRouterErrorProps
      });
    }

    childScope.$watch('viz.path', (newPath: string) => {

      if(!newPath) {
        displayInvalidVizError(CommonErrorCode.UnexpectedParameterValue, newPath);
        return;
      }

      const locationService = ServiceLocator.get<ng.ILocationService>('$location');
      const searchParams = locationService.search();

      this.props.views.getByPath(newPath)
        .then((viewInfo: IViewDetailInfo) => {
          this.loadNewViewInfo(viewInfo, childScope);

          if (childScope.displayRoutingError && !childScope.displayDataAlertRoutingError) {
            // We display routing error when we could not fetch the view. Since we could fetch it now,
            // reset the error codes and  update the state since fetching this view was successful.
            // If we displayed a data alert error, that means we fetched the view correctly before.

            childScope.displayRoutingError = false;

            childScope.updateVizProviderState({
              ...initialNoErrorState,
              viewInfo: viewInfo
            });
          }

          // because the viewInfo can return after we have navigated away from this page, we only want to call
          // enterSpace if we are still on the view page. Otherwise, all sorts of errors happen if the SpaceInfo
          // is wrong- TFSID 956506
          if (isCurrentUrlViz()) {
            appStore.dispatch(enterSpace(
              {
                item: viewInfo,
                spaceType: ViewDefinition.contentType,
                subspaceName: undefined
              }
            ));
          }

          //if fromEmail param in location.search then log the event
          if(searchParams[UrlParamKeys.FromFailureEmail]) {
              // Deprecated, use TelemetryService instead of EventFactory.
              childScope.deprecatedEventFactory.sendFromEmailEvent(
                  DeprecatedEventWrapper.eventTypes.VisitAlertFromEmail,
                  'Failure',
                  alertId,
                  viewInfo.id,
                  viewInfo.workbook.id,
                  this.props.server.sessionSiteRole());
          }
          if(searchParams[UrlParamKeys.FromSuccessEmail]) {
              // Deprecated, see TelemetryService instead.
              childScope.deprecatedEventFactory.sendFromEmailEvent(
                  DeprecatedEventWrapper.eventTypes.VisitAlertFromEmail,
                  'Success',
                  alertId,
                  viewInfo.id,
                  viewInfo.workbook.id,
                  this.props.server.sessionSiteRole());
          }

          maybeSendTelemetry_favoriteItemOpened(this.props.telemetry, viewInfo);
        })
        .catch((errorCode: number) => {
          // B153202, TFSID: 463801 - If user does not have permission to view the viz but guest does
          // then we recieve a GuestPermittedViewNotFoundError, so we can try to automatically
          // redirect to to the embedded view (if not a site or server admin). But if there's a regular
          // ViewNotFound error, there's no chance for guest to be able to view it, so just show the error here and now.
          if (errorCode === CommonErrorCode.GuestPermittedViewNotFound && this.props.server.isGuestEnabled() && !this.props.server.isSessionSiteOrServerAdmin()) {
            this.props.vizService.redirectToEmbeddedMode(false);
          } else {
            displayInvalidVizError(errorCode, newPath);
          }
        });

      //Check for errors in alertId part
      const alertId = searchParams[UrlParamKeys.EditDataAlert];
      const siteUrls = new SpacesSiteUrls({siteUrlName: this.props.server.sessionSiteUrlName(), queryString: window.location.search});

      //Using siteUrls.viewUrl($location.path()) would preserve query string, which won't work
      //So we're explicitly getting the view URL and explicity manually adding the hash
      const viewUrl = siteUrls.viewUrl(childScope.viz.path);

      if(alertId) {
        this.props.dataAlerts.getById(alertId)
          .then((dataAlert: IDataAlertInfo) => {
            //we don't want an alert error if there's a view error
            if (!!childScope.displayRoutingError) {
              return;
            }

            if (!dataAlert) {
              const routerErrorProps: RouterErrorOwnProps = {
                errorType: 'view-data-alert-exists',
                urls: siteUrls,
                dataAlertErrorData: {
                  viewUrl: viewUrl,
                  owner: undefined
                }
              };
              displayDataAlertError(routerErrorProps);

            } else if (dataAlert.owner.id !== this.props.server.sessionUserId() && !this.props.server.isSessionSiteOrServerAdmin()) {
              const routerErrorProps: RouterErrorOwnProps = {
                errorType: 'view-data-alert-permissions',
                urls: siteUrls,
                dataAlertErrorData: {
                  viewUrl: viewUrl,
                  owner: dataAlert.owner
                }
              };
              displayDataAlertError(routerErrorProps);

            } else if (this.props.server.isViewerUser()) {
              const routerErrorProps: RouterErrorOwnProps = {
                errorType: 'view-data-alert-viewer',
                urls: siteUrls,
                dataAlertErrorData: {
                  viewUrl: viewUrl,
                  owner: undefined
                }
              };
              displayDataAlertError(routerErrorProps);
            } else {
              childScope.displayDataAlertRoutingError = false;
            }
          })
          .catch((errorCode: number) => {
            const routerErrorProps: RouterErrorOwnProps = {
              errorType: 'view-data-alert-exists',
              urls: siteUrls,
              dataAlertErrorData: {
                viewUrl: viewUrl,
                owner: undefined
              }
            };
            displayDataAlertError(routerErrorProps);
          });
      }
    });

    childScope.$watch('viz.instanceId', (newInstanceId: string) => {
      if (newInstanceId) {
        this.props.vizService.updateInstanceId(childScope, newInstanceId);
      }
    });

    childScope.$watch('viz.isExplicitOriginalViz', (isExplicitOriginalViz: boolean) => {
      this.props.vizService.updateExplicitOriginalViz(childScope, isExplicitOriginalViz);
    });


    this.spaceScope = childScope;
    return childScope;
  }

  private loadNewViewInfo(viewInfo: IViewDetailInfo, childScope: any): ng.IPromise<any> {

    return this.props.projects.getProjectHierarchy(viewInfo.workbook.project.id).then((projectHierarchy: INamedItemInfo[]) => {
      this.props.browserTitleService.setVizTitle(viewInfo.name, viewInfo.workbook.name);
      childScope.vizProps.view = viewInfo;

      // Update breadcrumbs
      const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
      const siteUrls = this.props.urls;
      const workbook: IExplorableItemTypeInfo<'workbook'> = {
        id: viewInfo.workbook.id,
        name: viewInfo.workbook.name,
        contentType: 'workbook'
      };

      // because the project hierarchy can return after we have navigated away from this page, we only want to call
      // populateContentBreadcrumbs if we are still on the view page. Otherwise, the wrong breadcrumbs show.
      // TFSID 956506
      if (isCurrentUrlViz()) {
        populateContentBreadcrumbs([...projectHierarchyWithType, workbook, viewInfo], siteUrls);
      }

      this.props.views.markRecentlyViewed(viewInfo.id);

      return this.props.views.actions(viewInfo.id).then((actions: IViewActions) => {
        childScope.allowedAction = actions;
      });
    });
  }

  private updateState = (newState: Pick<VizSpaceProviderState, keyof VizSpaceProviderState>) : void => {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }
}

export namespace VizSpaceProviderAngularProvider {
  function getAngularServicesWithServiceLocator() {
    const browserTitleService = ServiceLocator.get<BrowserTitleService>('BrowserTitleService');
    const server = ServiceLocator.get<ServerService>('ServerService');
    const views = ServiceLocator.get<Views>('Views');
    const telemetry = ServiceLocator.get<TelemetryService>('TelemetryService');
    const dataAlerts = ServiceLocator.get<DataAlerts>('DataAlerts');
    const deprecatedAnalytics = ServiceLocator.get<DeprecatedAnalyticsService>('DeprecatedAnalyticsService');
    const dataSources = ServiceLocator.get<DataSources>('DataSources');
    const vizService = ServiceLocator.get<VizService>('VizService');
    const projects = ServiceLocator.get<Projects>('Projects');

    return {
      browserTitleService,
      server,
      views,
      telemetry,
      dataAlerts,
      deprecatedAnalytics,
      dataSources,
      vizService,
      projects
    };
  }

  function selectPropsFromAngularServices(angularServices: VizSpaceProviderAngularProps) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<VizSpaceProviderPropsWithoutAngular, VizSpaceProviderAngularProps, VizSpaceProviderAngularProps>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const VizSpaceProviderAng: React.ComponentType<VizSpaceProviderPropsWithoutAngular> = VizSpaceProviderAngularProvider.HOC(VizSpaceProvider);

const mapStateToProps: MapStateToProps<VizSpaceProviderStateProps> = (appState) => ({
  vizPath: selectVizPath(appState),
  sessionSiteUrlName: SessionSiteSelectors.siteUrlName(appState),
  layoutType: selectLayoutType(appState),
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(appState)
});

const mapDispatchToProps: MapDispatchToProps<VizSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  setVizPath,
  setSpaceInfoItem,
}, dispatch);

export const ConnectedVizSpaceProvider: React.ComponentClass<VizSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(VizSpaceProviderAng);
